import {
  Component, computed, effect, inject, input, NgZone, OnDestroy, OnInit, signal, Signal, viewChild, ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { filter, Observable, of, Subject, take, takeUntil } from 'rxjs';
import {
  BaseTreeModel, BrowserHelper, DropZoneHelper, DynamicComponentsHelper, NodePositionChangedEventModel, TreeDragDropService, TreeModel,
  TreeService, TooltipDirective, TreeComponent,
} from '@tailormap-viewer/shared';
import { map, tap } from 'rxjs/operators';
import { MenubarService } from '../../menubar';
import { TocMenuButtonComponent } from '../toc-menu-button/toc-menu-button.component';
import { Store } from '@ngrx/store';
import { AppLayerModel, AuthenticatedUserService, BaseComponentTypeEnum, TocConfigModel, WmsStyleModel } from '@tailormap-viewer/api';
import { MapService } from '@tailormap-viewer/map';
import { selectFilteredLayerTree, selectFilterEnabled } from '../state/toc.selectors';
import { toggleFilterEnabled } from '../state/toc.actions';
import {
  select3dTilesLayers, selectEditableLayers, selectIn3dView, selectLayersWithoutWebMercatorIds, selectSelectedNode, selectSelectedNodeId,
} from '../../../map/state/map.selectors';
import {
  moveLayerTreeNode, setLayerStyle, setLayerVisibility, toggleLevelExpansion, toggleSelectedLayerId,
} from '../../../map/state/map.actions';
import { selectFilteredLayerIdsWithSource } from '../../../state/filter-state/filter.selectors';
import { setEditActive, setSelectedEditLayer } from '../../edit/state/edit.actions';
import { ComponentConfigHelper } from '../../../shared';
import { toSignal } from '@angular/core/rxjs-interop';
import { TocFeatureRegistrationService } from '../services/toc-feature-registration.service';
import { TocNodeLayerComponent } from '../toc-node-layer/toc-node-layer.component';
import { TocNodeDetailsMobileComponent } from '../toc-node-details-mobile/toc-node-details-mobile.component';
import { ToggleAllLayersButtonComponent } from '../toggle-all-layers-button/toggle-all-layers-button.component';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TocFilterInputComponent } from '../toc-filter-input/toc-filter-input.component';
import { TocNodeDetailsComponent } from '../toc-node-details/toc-node-details.component';
import { AsyncPipe } from '@angular/common';

interface AppLayerTreeModel extends BaseTreeModel {
  metadata: AppLayerModel;
}
const isAppLayerTreeModel = (node: BaseTreeModel): node is AppLayerTreeModel => !!node.metadata && node.metadata.layerName;

@Component({
    selector: 'tm-toc',
    templateUrl: './toc.component.html',
    styleUrls: [ './toc.component.css', '../../../../../assets/layer-tree-style.css' ],
    providers: [ TreeService, TreeDragDropService ],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TocNodeLayerComponent,
        TocNodeDetailsMobileComponent,
        ToggleAllLayersButtonComponent,
        MatIconButton,
        TooltipDirective,
        MatIcon,
        TocFilterInputComponent,
        TreeComponent,
        TocNodeDetailsComponent,
        AsyncPipe,
    ],
})
export class TocComponent implements OnInit, OnDestroy {
  private store$ = inject(Store);
  private treeService = inject<TreeService<AppLayerModel>>(TreeService);
  private treeDragDropService = inject(TreeDragDropService);
  private menubarService = inject(MenubarService);
  private mapService = inject(MapService);
  private ngZone = inject(NgZone);
  private authenticatedUserService = inject(AuthenticatedUserService);
  private tocFeatureRegistrationService = inject(TocFeatureRegistrationService);

  private menuBarContainer = viewChild('menuBar', { read: ViewContainerRef });
  private belowTreeContainer = viewChild('belowTreeContainer', { read: ViewContainerRef });
  private aboveTreeContainer = viewChild('aboveTreeContainer', { read: ViewContainerRef });

  public mobileToc = input(false);

  private destroyed = new Subject();
  public visible$: Observable<boolean> = of(false);
  public scale: number | null = null;

  public infoVisible = signal(false);
  public infoTreeNode$ = this.store$.select(selectSelectedNode);
  public activeMobileInfoNodes = signal<string[]>([]);

  public filterEnabled$ = this.store$.select(selectFilterEnabled);
  public isMobileDevice = BrowserHelper.isTouchDevice;
  public dragDropEnabled = !this.isMobileDevice;
  public allLevelNodesCollapsed$ = this.treeService.allLevelNodesCollapsed$;
  public hasExpandableNodes$ = this.treeService.hasExpandableNodes$;
  public toggleExpandAllTooltip$ = this.allLevelNodesCollapsed$.pipe(
    map(collapsed => collapsed ? $localize `:@@core.toc.expand-all:Expand all groups` : $localize `:@@core.toc.collapse-all:Collapse all groups`),
  );
  public treeLabel = $localize `:@@core.toc.tree-label:Tree with available map layers.`;

  public in3D: Signal<boolean> = signal(false);
  public layersWithoutWebMercator: Signal<string[]> = signal([]);
  public tiles3DLayerIds: Signal<string[]> = signal([]);
  public filteredLayerIds: Signal<{ id: string; source: string }[]> = signal([]);

  private config = ComponentConfigHelper.componentConfigSignal<TocConfigModel>(this.store$, BaseComponentTypeEnum.TOC);

  private editComponentEnabled = ComponentConfigHelper.componentEnabledConfigSignal(this.store$, BaseComponentTypeEnum.EDIT);
  private authenticatedUserDetails = toSignal(this.authenticatedUserService.getUserDetails$());

  private editableLayerIds = computed(() => {
    const editableLayers = this.store$.selectSignal(selectEditableLayers);
    return editableLayers().map(layer => layer.id);
  });

  public getCurrentlyEditableLayerIds = computed(() => {
    return this.config()?.showEditLayerIcon
        && this.authenticatedUserDetails()?.isAuthenticated // remove when HTM-1762 is implemented
        && this.editComponentEnabled()
      ? this.editableLayerIds()
      : [];
  });

  constructor() {
    effect(() => {
      const components = this.tocFeatureRegistrationService.registeredAdditionalFeatures();
      const menuBarContainer = this.menuBarContainer();
      const belowTreeContainer = this.belowTreeContainer();
      const aboveTreeContainer = this.aboveTreeContainer();
      if (!menuBarContainer || !belowTreeContainer || !aboveTreeContainer) {
        return;
      }
      DynamicComponentsHelper.createComponentsForPosition(components, {
        'menuBar': menuBarContainer,
        'aboveTree': aboveTreeContainer,
        'belowTree': belowTreeContainer,
      });
    });
  }

  public ngOnInit(): void {
    this.visible$ = this.menubarService.isComponentVisible$(BaseComponentTypeEnum.TOC);
    this.mapService.getMapViewDetails$()
      .pipe(takeUntil(this.destroyed), map(resolution => resolution.scale))
      .subscribe(scale => this.scale = scale);

    this.treeService.setDataSource(
      this.store$.select(selectFilteredLayerTree),
    );
    this.treeService.setSelectedNode(this.store$.select(selectSelectedNodeId));
    this.treeDragDropService.setDragDropEnabled(!this.isMobileDevice);
    this.treeService.checkStateChangedSource$
      .pipe(
        takeUntil(this.destroyed),
        map(checkChange => checkChange
          .filter(isAppLayerTreeModel)
          .map(node => ({ id: node.metadata.id, checked: !!node.checked }))),
      )
      .subscribe(checkChanged => this.store$.dispatch(setLayerVisibility({ visibility: checkChanged })));
    this.treeService.nodeExpansionChangedSource$
      .pipe(takeUntil(this.destroyed))
      .subscribe(({ node }) => this.store$.dispatch(toggleLevelExpansion({ id: node.id })));
    this.treeService.selectionStateChangedSource$
      .pipe(
        takeUntil(this.destroyed),
        filter(isAppLayerTreeModel),
        map(node => node.metadata.id),
        tap(() => this.infoVisible.set(true)),
      )
      .subscribe(layerId => {
        this.store$.dispatch(toggleSelectedLayerId({ layerId }));
      });

    this.treeService.nodePositionChangedSource$
      .pipe(takeUntil(this.destroyed))
      .subscribe((evt) => this.handleNodePositionChanged(evt));

    this.menubarService.registerComponent({ type: BaseComponentTypeEnum.TOC, component: TocMenuButtonComponent });

    this.in3D = this.store$.selectSignal(selectIn3dView);
    this.layersWithoutWebMercator = this.store$.selectSignal(selectLayersWithoutWebMercatorIds);
    const tiles3DLayers = this.store$.selectSignal(select3dTilesLayers);
    this.tiles3DLayerIds = computed(() => tiles3DLayers().map(l => l.id));
    this.filteredLayerIds = this.store$.selectSignal(selectFilteredLayerIdsWithSource);

    if (this.mobileToc()) {
      this.visible$
        .pipe(takeUntil(this.destroyed))
        .subscribe(visible => {
          if (visible) {
            this.menubarService.setMobilePanelHeight(500);
          }
      });
    }
  }

  public getDropZoneConfig() {
    return DropZoneHelper.getDefaultDropZones(this.treeService);
  }

  public ngOnDestroy() {
    this.menubarService.deregisterComponent(BaseComponentTypeEnum.TOC);
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  public layerInfoClosed() {
    this.infoVisible.set(false);
  }

  public toggleLayerFilter() {
    this.store$.dispatch(toggleFilterEnabled());
  }

  public handleNodePositionChanged(evt: NodePositionChangedEventModel) {
    this.ngZone.run(() => {
      this.store$.dispatch(moveLayerTreeNode({
        nodeId: evt.nodeId,
        position: evt.position,
        parentId: evt.toParent || undefined,
        sibling: evt.sibling,
      }));
    });
  }

  public toggleReordering() {
    this.dragDropEnabled = !this.dragDropEnabled;
    this.treeDragDropService.setDragDropEnabled(this.dragDropEnabled);
  }

  public zoomToScale(minScale: number) {
    this.mapService.zoomToScale(minScale);
  }

  public editLayer(layer: string) {
    this.store$.dispatch(setSelectedEditLayer( { layer }));
    this.store$.dispatch(setEditActive({ active: true }));
  }

  public setShowMobileInfo(node: TreeModel<AppLayerModel>) {
    const activeMobileInfoNodes = this.activeMobileInfoNodes();
    if (activeMobileInfoNodes.includes(node.id)) {
      this.activeMobileInfoNodes.set(activeMobileInfoNodes.filter(id => id !== node.id));
    } else {
      this.activeMobileInfoNodes.set([ ...activeMobileInfoNodes, node.id ]);
    }
  }

  protected changeStyle({ layerId, selectedStyle }: { layerId: string; selectedStyle: WmsStyleModel }) {
    this.store$.dispatch(setLayerStyle({ style: [{ id: layerId, style: selectedStyle.name }] }));
  }

  public toggleExpandAll() {
    this.allLevelNodesCollapsed$.pipe(take(1)).subscribe(collapsed => {
      if (collapsed) {
        this.treeService.expandAllLevelNodes();
      } else {
        this.treeService.collapseAllLevelNodes();
      }
    });
  }
}
